import { FormControl, FormGroup, Validators } from '@angular/forms';
import { startWith } from 'rxjs/operators';
import { DateTimeUtility } from '../utility/date-time.utility';
import { BaseResponse } from './base-response.model';
import { HousingUnitJSON } from './housing-unit.model';
import { Validation } from './validation.model';

export class HousingSearchResponse extends BaseResponse {
	constructor(
		public message: string,
		public housingUnits: HousingUnitJSON[],
		public count: number,
		public page: number,
		public limit: number
	) {
		super(message);
	}
}

export interface HousingSearchFormGroup {
	currentTenantId: FormControl<number>;
	compound: FormControl<string>;
	unitType: FormControl<string>;
	numberOfBedrooms: FormControl<number>;
	useContractStartDate: FormControl<boolean>;
	contractStartDateAfter: FormControl<Date>;
	contractStartDateBefore: FormControl<Date>;
	useContractEndDate: FormControl<boolean>;
	contractEndDateAfter: FormControl<Date>;
	contractEndDateBefore: FormControl<Date>;
	includeArchived: FormControl<boolean>;
}

export class HousingSearch {
	constructor(
		public page: number,
		public currentTenantId: number,
		public compound: string,
		public unitType: string,
		public numberOfBedrooms: number,
		public useContractStartDate: boolean,
		public contractStartDateAfter: Date,
		public contractStartDateBefore: Date,
		public useContractEndDate: boolean,
		public contractEndDateAfter: Date,
		public contractEndDateBefore: Date,
		public includeArchived: boolean
	) {}

	static createFormGroup(
		search: HousingSearch,
		validation: Validation
	): FormGroup<HousingSearchFormGroup> {
		const fg = new FormGroup<HousingSearchFormGroup>({
			currentTenantId: new FormControl(
				search ? search.currentTenantId : null
			),
			compound: new FormControl(search ? search.compound : ''),
			unitType: new FormControl(search ? search.unitType : ''),
			numberOfBedrooms: new FormControl(
				search ? search.numberOfBedrooms : null,
				[
					Validators.maxLength(validation.number.maxLength),
					Validators.pattern(validation.number.pattern),
				]
			),
			useContractStartDate: new FormControl(
				search ? search.useContractStartDate : false
			),
			contractStartDateAfter: new FormControl(
				search ? search.contractStartDateAfter : null,
				[Validators.required]
			),
			contractStartDateBefore: new FormControl(
				search ? search.contractStartDateBefore : null,
				[Validators.required]
			),
			useContractEndDate: new FormControl(
				search ? search.useContractEndDate : false
			),
			contractEndDateAfter: new FormControl(
				search ? search.contractEndDateAfter : null,
				[Validators.required]
			),
			contractEndDateBefore: new FormControl(
				search ? search.contractEndDateBefore : null,
				[Validators.required]
			),
			includeArchived: new FormControl(
				search ? search.includeArchived : false
			),
		});
		fg.controls.useContractStartDate.valueChanges
			.pipe(startWith(fg.controls.useContractStartDate.value))
			.subscribe((enable: boolean) => {
				if (enable) {
					fg.controls.contractStartDateAfter.enable();
					fg.controls.contractStartDateBefore.enable();
				} else {
					fg.controls.contractStartDateAfter.disable();
					fg.controls.contractStartDateBefore.disable();
				}
			});
		fg.controls.useContractEndDate.valueChanges
			.pipe(startWith(fg.controls.useContractEndDate.value))
			.subscribe((enable: boolean) => {
				if (enable) {
					fg.controls.contractEndDateAfter.enable();
					fg.controls.contractEndDateBefore.enable();
				} else {
					fg.controls.contractEndDateAfter.disable();
					fg.controls.contractEndDateBefore.disable();
				}
			});
		return fg;
	}

	static fromFormGroup(fg: FormGroup<HousingSearchFormGroup>): HousingSearch {
		return Object.assign(Object.create(HousingSearch.prototype), {
			currentTenantId: fg.controls.currentTenantId.value,
			compound: fg.controls.compound.value,
			unitType: fg.controls.unitType.value,
			numberOfBedrooms: fg.controls.numberOfBedrooms.value
				? fg.controls.numberOfBedrooms.value
				: null,
			useContractStartDate: fg.controls.useContractStartDate.value,
			contractStartDateAfter: fg.controls.useContractStartDate.value
				? DateTimeUtility.toDatabaseDateString(
						fg.controls.contractStartDateAfter.value
					)
				: null,
			contractStartDateBefore: fg.controls.useContractStartDate.value
				? DateTimeUtility.toDatabaseDateString(
						fg.controls.contractStartDateBefore.value
					)
				: null,
			useContractEndDate: fg.controls.useContractEndDate.value,
			contractEndDateAfter: fg.controls.useContractEndDate.value
				? DateTimeUtility.toDatabaseDateString(
						fg.controls.contractEndDateAfter.value
					)
				: null,
			contractEndDateBefore: fg.controls.useContractEndDate.value
				? DateTimeUtility.toDatabaseDateString(
						fg.controls.contractEndDateBefore.value
					)
				: null,
			includeArchived: fg.controls.includeArchived.value,
		});
	}
}
